/**
 * Loan simulation helpers for OfferMockStep (Vento/Almango commerce frontend).
 * API rate contract: TNA decimal (0.95 = 95%).
 * FixedTermRateCalculator and DefaultLoanCalculator expect percentage (95).
 */

export const tnaDecimalToCalculatorPercent = (tnaDecimal) =>
  Number(tnaDecimal) * 100;

export const resolveOfferRate = (offer) =>
  Number(offer?.interestRate ?? offer?.rate ?? offer?.taxes ?? 0);

export const roundMoney2 = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const amountsEqual = (left, right) =>
  Math.abs(Number(left) - Number(right)) < 0.005;

export const clampAmountToPlan = (value, min, max) => {
  const numeric = Number(value);
  const planMin = Number(min);
  const planMax = Number(max);

  if (!Number.isFinite(numeric)) {
    return planMin;
  }

  if (numeric < planMin) {
    return planMin;
  }

  if (numeric > planMax) {
    return planMax;
  }

  return numeric;
};

export const resolveCanonicalPair = (offer, selectionMode) => {
  if (selectionMode === 'pre') {
    return {
      canonicalAmount: Number(offer?.maxAmount ?? offer?.amount ?? 0),
      canonicalInstallment: Number(
        offer?.installmentAmount ?? offer?.maxInstallmentAmount ?? 0
      )
    };
  }

  return {
    canonicalAmount: Number(offer?.amount ?? offer?.maxAmount ?? 0),
    canonicalInstallment: Number(
      offer?.installmentAmount ?? offer?.maxInstallmentAmount ?? 0
    )
  };
};

export const resolveResumeCanonicalPair = (resumeOffer) => ({
  canonicalAmount: Number(resumeOffer?.amount ?? 0),
  canonicalInstallment: Number(
    resumeOffer?.installmentAmount ?? resumeOffer?.monthlyPayment ?? 0
  )
});

export const shouldUseResumeAuthoritativeOffer = ({
  isOfferSelectionLocked,
  resumeOffer,
  planId,
  selectionMode,
  sliderAmount
}) => {
  if (!resumeOffer || selectionMode !== 'final') {
    return false;
  }

  const resumeOfferId = resumeOffer.offerId || resumeOffer.id;
  if (!resumeOfferId || resumeOfferId !== planId) {
    return false;
  }

  if (isOfferSelectionLocked) {
    return true;
  }

  return amountsEqual(sliderAmount, resumeOffer.amount);
};

export const resolvePlanMonthlyPayment = ({
  calculator,
  calculatorReady,
  sliderAmount,
  installments,
  rate,
  canonicalAmount,
  canonicalInstallment,
  useCanonicalInstallment = true
}) => {
  if (
    useCanonicalInstallment
    && amountsEqual(sliderAmount, canonicalAmount)
    && Number.isFinite(canonicalInstallment)
    && canonicalInstallment > 0
  ) {
    return {
      monthlyPayment: canonicalInstallment,
      isReady: true
    };
  }

  if (!calculatorReady || !calculator) {
    return {
      monthlyPayment: null,
      isReady: false
    };
  }

  try {
    calculator.setup({
      amount: sliderAmount,
      installments,
      interest: tnaDecimalToCalculatorPercent(rate)
    });
    const result = calculator.calculate();
    const calculatedAmount = result?.installments?.[0]?.amount;

    if (!Number.isFinite(Number(calculatedAmount))) {
      return {
        monthlyPayment: null,
        isReady: false
      };
    }

    return {
      monthlyPayment: roundMoney2(calculatedAmount),
      isReady: true
    };
  } catch (error) {
    return {
      monthlyPayment: null,
      isReady: false
    };
  }
};
